<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="600">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="updateSite">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Edit Harvester Site</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12">
                            <company-select v-model="formData['company_id']" :items="companies" :rules="[vRules.required]"
                                      density="compact" label="Company">
                            </company-select>
                            <v-text-field v-model="formData['name']" autofocus
                                          :rules="[vRules.required]" density="compact" label="Name"/>
                            <v-select v-model="formData['status']"
                                      :items="[{value:1,title:'Enabled'},{value:0,title:'Disabled'}]"
                                      :rules="[vRules.required]" density="compact" label="Status">
                            </v-select>
                        </v-col>

                        <v-col class="py-0" cols="12">
                            <v-textarea v-model="formData['description']"
                                        :rules="[vRules.required]" density="compact" label="Description"/>
                        </v-col>

                        <v-col cols="12" style="height: 300px">
                            <l-map ref="mapContainer"
                                   :center="[(formData.latitude??47.41322), (formData.longitude??-1.219482)]" :zoom="10"
                                   @ready="mapIsReady">
                                <l-tile-layer :url="$mapTiles.street"/>
                            </l-map>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import L from "leaflet";

import "leaflet-draw";
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {mapTiles} from "@/composables/mapConfig";
import CompanySelect from "@/components/globals/company-select.vue";

const companies = ref([]);
const mapContainer = ref(null);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    site: Object
});
const formData = reactive({
    valid: false,
    name: props.site.name ?? null,
    status: props.site.status ?? 0,
    description: props.site.description ?? null,
    company_id: props.site.company_id ?? null,
    polygon_coordinates: props.site.polygon_coordinates?.map((coordinate) => {
        return {'lat': coordinate.latitude, 'lng': coordinate.longitude};
    }) ?? []
});
const emits = defineEmits(['update:modelValue', 'updateSiteData']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);

onMounted(() => {
    fetchCompanies();
})

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    const geoSearchOptions = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider: new OpenStreetMapProvider({searchUrl: mapTiles.searchUrl, reverseUrl: mapTiles.reverseUrl}),
    });

    map.addControl(geoSearchOptions);
    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: false,
            polygon: {
                showArea: true,
                allowIntersection: false, // Restricts shapes to simple polygons
                drawError: {
                    color: '#9d1212', // Color the shape will turn when intersects
                    message: '<strong>Oh snap!<strong> you can\'t draw that!' // Message that will show when intersect
                }
            },
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems
        }
    });
    map.addControl(drawControl);

    const polygon = L.polygon(formData.polygon_coordinates, {
        // editable: true,
    });
    drawnItems.addLayer(polygon);
    if (formData['polygon_coordinates']) {
        map.fitBounds(polygon.getBounds());
    }

    map.on('draw:created', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });

        if (e.layerType === 'polygon') {
            formData['location_changed'] = 1;
            formData['polygon_coordinates'] = e.layer.getLatLngs()[0];
        }
        drawnItems.addLayer(e.layer);
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer(function (layer) {
            if (layer instanceof L.Polygon) {
                formData['location_changed'] = 1;
                formData['polygon_coordinates'] = layer.getLatLngs()[0];
            }
        });
    });

    map.on('draw:deleted', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        formData['location_changed'] = 1;
        formData['polygon_coordinates'] = null;
    });
}

const fetchCompanies = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Companies could not be loaded', 'error')
        })
}
const updateSite = () => {
    errors.value = [];
    send('put', `harvesters/sites/${props.site.id}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateSiteData', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
